/**
 * MaskedBestOddsPreview - Public landing page preview for the "Best Odds / Market
 * Anomalies" product.
 *
 * Mirrors MaskedSurebetPreview's data-fetching conventions (public endpoint,
 * loading/error/empty states, live demo section) but renders a card grid
 * instead of a table, per the Best Odds UX model: each card is a single
 * market anomaly ("NEW BEST ODDS") rather than a multi-margin event row.
 */

import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '../../i18n/hooks';
import BestOddsService from '../services/BestOddsService';
import { getMarginTypeName, getOutcomeName, getBookmakerName } from '../../surebets/utils/surebetUtils';
import NumberUtils from '../../utils/NumberUtils';
import './MaskedBestOddsPreview.css';

const EMPTY_PREVIEW = {
  maskedItems: [],
  demoItem: null,
  totalActiveAnomalies: 0
};

const MaskedBestOddsPreview = ({ onActivate, showHeader = true, maxItems = 3 }) => {
  const { t, language } = useTranslation();
  const [maskedItems, setMaskedItems] = useState([]);
  const [demoItem, setDemoItem] = useState(null);
  const [totalActiveAnomalies, setTotalActiveAnomalies] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchMaskedData = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await BestOddsService.getMaskedAnomalies();

        if (!isMounted) return;

        if (fetchError) {
          setError(fetchError);
        } else {
          const preview = data && data.maskedItems !== undefined ? data : EMPTY_PREVIEW;
          setMaskedItems(preview.maskedItems || []);
          setDemoItem(preview.demoItem || null);
          setTotalActiveAnomalies(preview.totalActiveAnomalies || 0);
        }
      } catch (err) {
        if (isMounted) {
          setError('Failed to load data');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchMaskedData();

    return () => {
      isMounted = false;
    };
  }, []);

  const topAnomalies = useMemo(() => {
    return [...maskedItems]
      .sort((a, b) => (b.spread || 0) - (a.spread || 0))
      .slice(0, maxItems);
  }, [maskedItems, maxItems]);

  const renderDemoCard = (anomaly) => {
    const marketLabel = getMarginTypeName(anomaly.marketKey, language);
    const outcomeLabel = getOutcomeName(anomaly.marketKey, anomaly.outcomeType, language);
    const bookmakerName = getBookmakerName(anomaly.bestBookmaker);

    return (
      <div
        key={`demo-${anomaly.id}`}
        className="best-odds-preview-card best-odds-preview-card--demo best-odds-preview-card--demo-compact"
      >
        <span className="best-odds-preview-card__badge best-odds-preview-card__badge--alert">
          {t('landing.bestOddsPreview.newBestOddsBadge', '🚨 НОВА ПАЗАРНА АНОМАЛИЯ')}
        </span>

        <p className="best-odds-preview-card__demo-event">
          ⚽ {anomaly.event.teamOne} – {anomaly.event.teamTwo} | {marketLabel}{' '}
          {t('landing.bestOddsPreview.outcomeLabel', 'Изход')}: {outcomeLabel}
        </p>

        <p className="best-odds-preview-card__demo-line">
          🥇 {t('landing.bestOddsPreview.maxValueLabel', 'Максимална стойност')}:{' '}
          {NumberUtils.formatOdds(anomaly.bestOdds, { decimals: 2 })} ({bookmakerName})
        </p>

        <p className="best-odds-preview-card__demo-line">
          📉 {t('landing.bestOddsPreview.marketAvgLabel', 'Средна за пазара')}:{' '}
          {NumberUtils.formatOdds(anomaly.marketAvg, { decimals: 2 })}
        </p>

        <p className="best-odds-preview-card__demo-line best-odds-preview-card__demo-line--spread">
          📈 {t('landing.bestOddsPreview.spreadLabel', 'Отклонение (Spread)')}:{' '}
          +{NumberUtils.formatPercentage(anomaly.spread, { decimals: 2, showSymbol: true })}
        </p>

        {onActivate && (
          <button
            type="button"
            className="unlock-btn best-odds-preview-card__cta"
            onClick={onActivate}
          >
            {t('landing.bestOddsPreview.unlockAllButton', 'Отключи всички')}
          </button>
        )}
      </div>
    );
  };

  const renderAnomalyCard = (anomaly, { isDemo = false } = {}) => {
    if (isDemo) {
      return renderDemoCard(anomaly);
    }

    const marketLabel = getMarginTypeName(anomaly.marketKey, language);
    const outcomeLabel = getOutcomeName(anomaly.marketKey, anomaly.outcomeType, language);
    const isMaskedEvent = anomaly.event?.teamOne === '***';
    const isMaskedBookmaker = anomaly.bestBookmaker === '***';

    return (
      <div
        key={`masked-${anomaly.id}`}
        className="best-odds-preview-card best-odds-preview-card--masked"
      >
        <div className="best-odds-preview-card__event">
          {isMaskedEvent ? (
            <span className="best-odds-preview-card__event-hidden">
              🔒 {t('landing.bestOddsPreview.eventHiddenLabel', 'Скрито събитие')}
            </span>
          ) : (
            <>
              <div className="best-odds-preview-card__teams">
                <span className="team-name">{anomaly.event.teamOne}</span>
                <span className="vs">vs</span>
                <span className="team-name">{anomaly.event.teamTwo}</span>
              </div>
              <div className="best-odds-preview-card__meta">
                <span>{anomaly.event.date}</span>
                <span>{anomaly.event.time}</span>
                <span>{anomaly.event.championship}</span>
              </div>
            </>
          )}
        </div>

        <div className="best-odds-preview-card__market">{marketLabel}</div>

        <div className="best-odds-preview-card__best-odds">
          <span className="best-odds-preview-card__badge">
            {t('landing.bestOddsPreview.newBestOddsBadge', '🚨 НОВА ПАЗАРНА АНОМАЛИЯ')}
          </span>
          <div className="best-odds-preview-card__best-odds-row">
            <span className="best-odds-preview-card__outcome">{outcomeLabel}</span>
            <span className="best-odds-preview-card__odds-value">
              {NumberUtils.formatOdds(anomaly.bestOdds, { decimals: 2 })}
            </span>
            <span className="best-odds-preview-card__bookmaker">
              {isMaskedBookmaker
                ? `🔒 ${t('landing.bestOddsPreview.bookmakerHiddenLabel', 'Скрит букмейкър')}`
                : getBookmakerName(anomaly.bestBookmaker)}
            </span>
          </div>
        </div>

        <div className="best-odds-preview-card__stats">
          <div className="best-odds-preview-card__stat">
            <span className="best-odds-preview-card__stat-label">
              {t('landing.bestOddsPreview.marketAvgLabel', 'Пазарна средна')}
            </span>
            <span className="best-odds-preview-card__stat-value">
              {NumberUtils.formatOdds(anomaly.marketAvg, { decimals: 2 })}
            </span>
          </div>
          <div className="best-odds-preview-card__stat best-odds-preview-card__stat--spread">
            <span className="best-odds-preview-card__stat-label">
              {t('landing.bestOddsPreview.spreadLabel', 'Отклонение')}
            </span>
            <span className="best-odds-preview-card__stat-value best-odds-preview-card__stat-value--positive">
              +{NumberUtils.formatPercentage(anomaly.spread, { decimals: 1, showSymbol: true })}
            </span>
          </div>
        </div>

        {onActivate && (
          <button
            type="button"
            className="unlock-btn best-odds-preview-card__cta"
            onClick={onActivate}
          >
            {t('landing.bestOddsPreview.unlockButton', 'Отключи детайли')}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="masked-best-odds-preview">
      <div className="masked-best-odds-container">
        {showHeader && (
          <div className="masked-best-odds-header">
            <h2 className="masked-best-odds-title">
              {t('landing.bestOddsPreview.title', 'Топ Пазарни Аномалии')}
            </h2>
            <p className="masked-best-odds-subtitle">
              {t('landing.bestOddsPreview.subtitle', 'Абонирайте се за пълен достъп до всички Best Odds сигнали в реално време')}
            </p>

            {!loading && !error && (
              <div className="masked-best-odds-stats">
                <div className="stat-card">
                  <span className="stat-value">{totalActiveAnomalies}</span>
                  <span className="stat-label">
                    {t('landing.bestOddsPreview.totalLabel', 'Активни аномалии')}
                  </span>
                </div>
                <div className="stat-card">
                  <span className="stat-value">{topAnomalies.length}</span>
                  <span className="stat-label">
                    {t('landing.bestOddsPreview.shownLabel', 'Показани (топ по отклонение)')}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="masked-best-odds-loading">
            <div className="loading-spinner" />
            <p>{t('landing.bestOddsPreview.loading', 'Зареждане на пазарни данни...')}</p>
          </div>
        )}

        {error && (
          <div className="masked-best-odds-error">
            <p>{t('landing.bestOddsPreview.error', 'Грешка при зареждане')}</p>
            {onActivate && (
              <button className="unlock-btn" onClick={onActivate}>
                {t('landing.bestOddsPreview.activateButton', 'Активирай абонамент')}
              </button>
            )}
          </div>
        )}

        {!loading && !error && topAnomalies.length > 0 && (
          <div className="masked-best-odds-grid">
            {topAnomalies.map((anomaly) => renderAnomalyCard(anomaly, { isDemo: false }))}
          </div>
        )}

        {!loading && !error && topAnomalies.length === 0 && !demoItem && (
          <div className="masked-best-odds-empty">
            <p>{t('landing.bestOddsPreview.empty', 'Няма открити пазарни аномалии в момента')}</p>
            {onActivate && (
              <button className="unlock-btn" onClick={onActivate}>
                {t('landing.bestOddsPreview.activateButton', 'Активирай абонамент')}
              </button>
            )}
          </div>
        )}
      </div>

      {!loading && !error && demoItem && (
        <section className="masked-best-odds-preview__live-demo" aria-labelledby="best-odds-live-demo-title">
          <div className="masked-best-odds-preview__live-demo-header">
            <h3 id="best-odds-live-demo-title" className="masked-best-odds-preview__live-demo-title">
              {t('landing.bestOddsLiveDemo.title', 'LIVE DEMO: Актуална аномалия в момента')}
            </h3>
          </div>

          {renderAnomalyCard(demoItem, { isDemo: true })}
        </section>
      )}
    </div>
  );
};

MaskedBestOddsPreview.propTypes = {
  onActivate: PropTypes.func,
  showHeader: PropTypes.bool,
  maxItems: PropTypes.number
};

export default MaskedBestOddsPreview;
